<template>
  <b-card>
    <b-card-title class="d-flex flex-column flex-md-row justify-content-between align-md-items-center">
      <span class="mb-2">Product Outcomes</span>
      <b-form-group class="mb-0">
        <b-form-radio-group
          v-model="selectedChart"
          :options="chartOptions"
          name="outcomes-chart-selection"
          button-variant="outline-primary"
          buttons
        />
      </b-form-group>
    </b-card-title>
    <b-card-sub-title class="pb-00">
      <span v-show="selectedChart === 'annualised'">
        Distribution of annualised return in 2.5% buckets
      </span>
      <span v-show="selectedChart === 'payoff'">
        Distribution of product payoff
      </span>
    </b-card-sub-title>
    <b-card-text>
      <div v-if="loading" style="min-height: 370px;">
        <loading-container />
      </div>
      <div v-else style="min-height: 370px; height: 370px;">
        <app-echart-bar
          ref="barChart"
          :key="chartKey"
          :option-data="selectedChart === 'annualised' ? annualisedChartData : payoffChartData"
          style="height: 100%; width: 100%;"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardSubTitle, BCardText, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from './offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardText,
    BFormGroup,
    BFormRadioGroup,
    AppEchartBar,
    LoadingContainer,
  },
  props: {
    figureSix: {
      type: Object,
      default: () => ({
        annualised: {},
        payoff: {},
      }),
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      selectedChart: 'annualised',
      chartOptions: [
        { text: 'Annualised Return', value: 'annualised' },
        { text: 'Product Payoff', value: 'payoff' },
      ],
      chartKey: 0,
      annualisedChartData: {},
      payoffChartData: {},
      cachedAnnualisedImage: null,
      cachedPayoffImage: null,
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    figureSix: {
      handler(newData) {
        if (newData && newData.annualised && newData.payoff) {
          this.processOutcomesData(newData)
        }
      },
      immediate: true,
      deep: true,
    },
    selectedChart() {
      // Increment key to force chart re-render when switching charts
      this.chartKey += 1
    },
    skin() {
      // Re-process data when theme changes
      if (this.figureSix && this.figureSix.annualised && this.figureSix.payoff) {
        this.processOutcomesData(this.figureSix)
      }
    },
  },
  methods: {
    getDataURL() {
      return (this.selectedChart === 'annualised' ? this.cachedAnnualisedImage : this.cachedPayoffImage) || null
    },
    getBothDataURLs() {
      return {
        annualised: this.cachedAnnualisedImage || null,
        payoff: this.cachedPayoffImage || null,
      }
    },
    processOutcomesData(figureData) {
      // Annualised Return Chart
      this.annualisedChartData = {
        legend: {
          show: false,
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: 20,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => {
            if (params && params.length > 0) {
              let result = `${params[0].name}<br/>`
              params.forEach(param => {
                result += `${param.marker} ${param.seriesName}: ${param.value}%<br/>`
              })
              return result
            }
            return ''
          },
        },
        xAxis: {
          type: 'category',
          data: figureData.annualised.xAxisBuckets || [],
          name: 'Annualised return (%)',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 11,
            rotate: 45,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Probabilities (%)',
          nameLocation: 'middle',
          nameGap: 50,
          min: 0,
          max: 100,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: 'Annualised Return',
            type: 'bar',
            data: figureData.annualised.barData ? figureData.annualised.barData.map(val => Math.round(parseFloat(val) * 100) / 100) : [],
            itemStyle: {
              color: 'lightblue',
            },
          },
        ],
      }

      // Product Payoff Chart
      this.payoffChartData = {
        legend: {
          show: false,
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: 20,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => {
            if (params && params.length > 0) {
              let result = `${params[0].name}<br/>`
              params.forEach(param => {
                result += `${param.marker} ${param.seriesName}: ${param.value}%<br/>`
              })
              return result
            }
            return ''
          },
        },
        xAxis: {
          type: 'category',
          data: figureData.payoff.xAxisBuckets || [],
          name: 'Product payoff (%)',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 11,
            rotate: 45,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Probabilities (%)',
          nameLocation: 'middle',
          nameGap: 50,
          min: 0,
          max: 100,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: 'Product Payoff',
            type: 'bar',
            data: figureData.payoff.barData ? figureData.payoff.barData.map(val => Math.round(parseFloat(val) * 100) / 100) : [],
            itemStyle: {
              color: 'lightblue',
            },
          },
        ],
      }

      this.cachedAnnualisedImage = renderChartToBase64(this.annualisedChartData)
      this.cachedPayoffImage = renderChartToBase64(this.payoffChartData)
      this.loading = false
    },
  },
}
</script>
